import { Component, computed, inject, signal, ViewChild } from '@angular/core';
import { Validators } from '@angular/forms';
import { HttpErrorResponse } from '@angular/common/http';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { AlertService, AlertType, ColumnConfig, ConfirmationDialogService, FieldType, FilterConfig, GenericTableComponent, GlobalAction, HeaderConfigurationService, ModalMode, ModalTabConfig, TableAction, TableActionConfig } from '@solcre-org/core-ui';
import { environment } from '../../../../environments/environment.local';
import { CountryHolidayModel } from '../../../shared/models/country-holiday.model';
import { Country } from '../../../shared/models/country.model';
import { COUNTRIES } from '../../../shared/data/countries';
import { DateUtils } from '../../../shared/utils/date.utils';
import { CountryHolidayService } from '../../../shared/services/country-holiday.service';

@Component({
  selector: 'app-country-holidays',
  imports: [GenericTableComponent, TranslateModule],
  templateUrl: './country-holidays.html',
})
export class CountryHolidays {

  private headerConfigurationService = inject(HeaderConfigurationService);
  private translateService: TranslateService = inject(TranslateService);
  private countryHolidayService: CountryHolidayService = inject(CountryHolidayService);
  private alertService: AlertService = inject(AlertService);
  private confirmService: ConfirmationDialogService = inject(ConfirmationDialogService);

  @ViewChild(GenericTableComponent) table!: GenericTableComponent<CountryHolidayModel>;

  endpoint = environment.API.COUNTRY_HOLIDAYS_URI;
  countries: Country[] = COUNTRIES;

  private currentYear = new Date().getFullYear();
  yearOptions: number[] = [this.currentYear - 1, this.currentYear, this.currentYear + 1, this.currentYear + 2, this.currentYear + 3];

  columns: ColumnConfig<CountryHolidayModel>[] = [
    {
      label: 'countryHolidays.country',
      key: 'country',
    },
    {
      label: 'countryHolidays.holidayDate',
      key: 'holidayDate',
      format: (value: string) => DateUtils.cleanApiDate(value),
    },
    {
      label: 'countryHolidays.name',
      key: 'name',
    },
    {
      label: 'countryHolidays.source',
      key: 'source',
      format: (value: string) => this.translateService.instant(`countryHolidays.sourceOptions.${value}`),
    },
    {
      label: 'countryHolidays.createdAt',
      key: 'createdAt',
      sortable: true,
      format: (value: any) => DateUtils.format(value),
    },
  ];

  modalFields = computed<ModalTabConfig<CountryHolidayModel>[]>(() => [
    {
      tabId: 'general_data',
      tabLabel: 'countryHolidays.general_data',
      fields: [
        {
          label: 'countryHolidays.country',
          placeholder: 'countryHolidays.countryPlaceholder',
          key: 'country',
          type: FieldType.SELECT,
          searchable: true,
          defaultValue: '',
          options: this.countries,
          readonly: false,
          validators: [Validators.required],
          errorMessages: {
            required: this.translateService.instant('validators.required'),
          }
        },
        {
          label: 'countryHolidays.holidayDate',
          placeholder: 'countryHolidays.holidayDatePlaceholder',
          key: 'holidayDate',
          type: FieldType.DATE,
          readonly: false,
          validators: [Validators.required],
          errorMessages: {
            required: this.translateService.instant('validators.required'),
          }
        },
        {
          label: 'countryHolidays.name',
          placeholder: 'countryHolidays.namePlaceholder',
          key: 'name',
          type: FieldType.TEXT,
          readonly: false,
        },
        {
          label: 'countryHolidays.source',
          key: 'source',
          type: FieldType.TEXT,
          readonly: true,
          modes: {
            [ModalMode.CREATE]: { visible: false, includeInPayload: false },
            [ModalMode.EDIT]: { visible: true, includeInPayload: false },
            [ModalMode.VIEW]: { visible: true, includeInPayload: false },
          },
        },
        {
          label: 'countryHolidays.createdAt',
          key: 'createdAt',
          type: FieldType.DATE,
          readonly: true,
          modes: {
            [ModalMode.CREATE]: { visible: false, includeInPayload: false },
            [ModalMode.EDIT]: { visible: false, includeInPayload: false },
            [ModalMode.VIEW]: { visible: true, includeInPayload: false },
          },
        },
        {
          label: 'countryHolidays.updatedAt',
          key: 'updatedAt',
          type: FieldType.DATE,
          readonly: true,
          modes: {
            [ModalMode.CREATE]: { visible: false, includeInPayload: false },
            [ModalMode.EDIT]: { visible: false, includeInPayload: false },
            [ModalMode.VIEW]: { visible: true, includeInPayload: false },
          },
        },
      ],
    },
  ] as any as ModalTabConfig<CountryHolidayModel>[]);

  customFilters = computed<FilterConfig<CountryHolidayModel>[]>(() => [
    {
      key: 'country',
      label: 'countryHolidays.country',
      placeholder: 'countryHolidays.countryPlaceholder',
      type: FieldType.SELECT,
      searchable: true,
      options: this.countries,
      readonly: false,
      filterFn: (row: CountryHolidayModel, value: any) => value,
    },
    {
      key: 'year',
      label: 'countryHolidays.year',
      placeholder: 'countryHolidays.yearPlaceholder',
      type: FieldType.SELECT,
      defaultValue: this.currentYear,
      options: this.yearOptions.map((year) => ({ label: String(year), value: year })),
      readonly: false,
      filterFn: (row: CountryHolidayModel, value: any) => value,
    },
  ]);

  activeFilters = signal<Map<string, any>>(new Map());
  isSyncing = signal(false);

  globalActions: GlobalAction<CountryHolidayModel>[] = [
    {
      label: this.translateService.instant('countryHolidays.sync.action'),
      icon: 'fa-regular fa-rotate',
      callback: () => this.synchronize(),
    },
  ];
  actions: TableActionConfig[] = [
    { action: TableAction.VIEW, isExtra: false },
    { action: TableAction.EDIT, isExtra: false, shouldShow: (row: CountryHolidayModel) => row.source !== 'nager' },
    { action: TableAction.DELETE, isExtra: true, shouldShow: (row: CountryHolidayModel) => row.source !== 'nager' },
    { action: TableAction.CREATE, isExtra: false },
  ];

  countryHolidayModelFactory = (data: Partial<CountryHolidayModel>): CountryHolidayModel => {
    return new CountryHolidayModel().fromJSON(data);
  };

  handleApiError = (error: any, defaultMessage: string, operation: string): string => {
    const errObj = error?.error || error;
    if (errObj?.status === 422 && Array.isArray(errObj?.violations)) {
      const duplicateViolation = errObj.violations.find((v: any) =>
        v.propertyPath === 'holidayDate' && v.message?.toLowerCase().includes('unique') ||
        v.message?.toLowerCase().includes('this combination')
      );
      if (duplicateViolation) {
        return this.translateService.instant('countryHolidays.errors.duplicateHoliday');
      }

      const fieldViolation = errObj.violations[0];
      if (fieldViolation) {
        return this.translateService.instant('countryHolidays.errors.invalidField', {
          field: this.translateService.instant(`countryHolidays.${fieldViolation.propertyPath}`),
          message: fieldViolation.message,
        });
      }
    }
    return defaultMessage;
  };

  onFilterChange(filters?: Map<string, any>) {
    this.activeFilters.set(filters ?? new Map());
  }

  synchronize(): void {
    if (this.isSyncing()) {
      return;
    }

    const filters = this.activeFilters();

    this.confirmService
      .openWithFields({
        title: this.translateService.instant('countryHolidays.sync.confirmTitle'),
        message: this.translateService.instant('countryHolidays.sync.confirmMessage'),
        fieldsMode: ModalMode.CREATE,
        fieldsData: {
          country: filters.get('country') ?? '',
          year: filters.get('year') ?? this.currentYear,
        },
        fieldsConfig: [
          {
            label: 'countryHolidays.country',
            placeholder: 'countryHolidays.countryPlaceholder',
            key: 'country',
            type: FieldType.SELECT,
            searchable: true,
            options: this.countries,
            validators: [Validators.required],
            errorMessages: {
              required: this.translateService.instant('validators.required'),
            },
          },
          {
            label: 'countryHolidays.year',
            placeholder: 'countryHolidays.yearPlaceholder',
            key: 'year',
            type: FieldType.SELECT,
            options: this.yearOptions.map((year) => ({ label: String(year), value: year })),
            validators: [Validators.required],
            errorMessages: {
              required: this.translateService.instant('validators.required'),
            },
          },
        ] as any,
      })
      .subscribe((result) => {
        if (!result.value) {
          return;
        }
        const country = result.fieldsData?.['country'];
        const year = result.fieldsData?.['year'];
        if (!country || !year) {
          return;
        }
        this.performSync(country, Number(year));
      });
  }

  private performSync(country: string, year: number): void {
    this.isSyncing.set(true);
    this.countryHolidayService.synchronize(country, year).subscribe({
      next: (result) => {
        this.isSyncing.set(false);
        this.alertService.show({
          type: AlertType.SUCCESS,
          message: this.translateService.instant('countryHolidays.sync.summary', {
            created: result.created,
            updated: result.updated,
            preservedManual: result.preservedManual,
            skipped: result.skipped,
          }),
          duration: 4000,
        });
        const syncedFilters = new Map<string, any>([
          ['country', country],
          ['year', year],
        ]);
        this.activeFilters.set(syncedFilters);
        this.table.applyCustomFilters(syncedFilters);
      },
      error: (error: HttpErrorResponse) => {
        this.isSyncing.set(false);
        if (error.status === 422) {
          this.alertService.show({
            type: AlertType.ERROR,
            message: error.error?.detail ?? this.translateService.instant('countryHolidays.sync.errors.validation'),
          });
          return;
        }
        if (error.status === 502) {
          this.alertService.show({
            type: AlertType.ERROR,
            message: this.translateService.instant('countryHolidays.sync.errors.upstream'),
          });
          return;
        }
        this.alertService.show({
          type: AlertType.ERROR,
          message: this.translateService.instant('errors.generic'),
        });
      },
    });
  }

  ngOnInit() {
    this.headerConfigurationService.setEndpointHeaders(this.endpoint, {
      patch: [
        { 'Content-Type': 'application/merge-patch+json' },
      ]
    });
  }
}
